import { useState, useEffect } from "react";
import "./style.css";
import { ReactComponent as Plus } from "../../Assets/icon/plus.svg";
import { ReactComponent as Minus } from "../../Assets/icon/minus.svg";
import { billingPortal } from "../../Services/subscription";
import GenericModal from "../Modal/GenericModal";

const AddMembersModal = ({
  isAddOpen,
  setisAddOpen,
  setisInviteOpen,
  memberContIncrement,
  secondary_price,
  products,
  subscriptionData,
  planName,
  billingCycle,
  setError,
}) => {
  const [memberCount, setMemberCount] = useState(1);
  const [currentMemberIndex, setCurrentMemberIndex] = useState(0);
  const [inviteComplete, setInviteComplete] = useState(false);
  const pricePerAccount = typeof secondary_price === 'number' ? secondary_price : undefined;
  const [loading, setLoading] = useState(false);

  const handleIncrease = () => setMemberCount(memberCount + 1);
  const handleDecrease = () =>
    memberCount > 1 && setMemberCount(memberCount - 1);
  const totalPrice = typeof pricePerAccount === 'number' ? memberCount * pricePerAccount : undefined;

  // Reset flow when modal is opened/closed
  useEffect(() => {
    if (isAddOpen) {
      setCurrentMemberIndex(0);
      setInviteComplete(false);
    }
  }, [isAddOpen]);

  // Pass member count to parent component
  useEffect(() => {
    memberContIncrement(memberCount);
  }, [memberCount, memberContIncrement]);

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const subscriptionId = subscriptionData?.subscription_id || subscriptionData?.id;
      if (!subscriptionId) {
        throw new Error("Missing subscription id to open billing portal.");
      }

      // Use the subscription's own current price_id directly
      const currentPriceId = subscriptionData?.metadata?.current_price?.price_id
        || subscriptionData?.plan?.provider_plan_id?.stripe;
      const allowedPriceIds = currentPriceId ? [currentPriceId] : [];

      const payload = {
        return_url: `${origin}/manageMembership`,
        type: "add_member",
        payload: {
          subscription_id: subscriptionId,
          allowed_price_ids: allowedPriceIds,
          disable_cancellation: true,
        },
      };

      const res = await billingPortal(payload);
      const url = res?.url || res?.payload?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error("No billing portal URL returned");
    } catch (error) {
      console.error("Error opening billing portal:", error);
      setError(
        error?.response?.data?.message || error?.message || "Failed to open billing portal. Please try again later."
      );
      setisAddOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Start the invite flow for the first member
  const startInviteFlow = () => {
    setCurrentMemberIndex(0);
    setisInviteOpen(true); // Open InvitetoAris modal for the first member
  };

  // This function should be called when each invite is completed
  // It can be passed to InvitetoAris as a callback
  const handleInviteComplete = () => {
    // Move to the next member
    const nextIndex = currentMemberIndex + 1;
    setCurrentMemberIndex(nextIndex);

    if (nextIndex < memberCount) {
      // There are more members to invite, keep the invite modal open
      // The InvitetoAris component should refresh with new empty fields
      // You'll need to modify InvitetoAris to accept the current index
      setisInviteOpen(true);
    } else {
      // All members have been invited
      setisInviteOpen(false);
      setInviteComplete(true);
      // You can trigger any final action here (e.g., show a success message)
    }
  };

  return (
    <GenericModal
      isOpen={isAddOpen}
      onClose={() => setisAddOpen(false)}
      heading="Add a Membership"
      primaryLabel={loading ? 'Processing...' : (memberCount > 1 ? "Add Memberships" : "Add Membership")}
      secondaryLabel="Cancel"
      onPrimary={handlePurchase}
      onSecondary={() => setisAddOpen(false)}
      loading={loading}
      maxWidth="500px"
    >
      <div className="add-membership-modal-content">
        <div className="plan-info-display">
          <div className="plan-info-label">Plan</div>
          <div className="plan-info-name">{planName || 'Aris'}</div>
          <div className="plan-info-billing">
            ${typeof secondary_price === 'number' ? secondary_price.toFixed(2) : '-'}/{billingCycle === 'yearly' ? 'yr' : 'mo'}, Billed {billingCycle === 'yearly' ? 'annually' : 'monthly'}
          </div>
        </div>
        <div className="membershipmodal-body">
              <div className="membership-count-container">
                <div className="pricing-c ounter-amount">
                  <div className="pricing-member-counter">
                    <button
                      className="pricing-counter-button"
                      onClick={handleDecrease}
                    >
                      <Minus />
                    </button>
                    <span className="pricing-counter-value">{memberCount}</span>
                    <button
                      className="pricing-counter-button"
                      onClick={handleIncrease}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
                <div className="membership-price">
                  {typeof pricePerAccount === 'number' ? `$${pricePerAccount}` : '-'}{" "}
                  <span className="membership-price-text-1">
                    {`/${billingCycle === 'yearly' || billingCycle === 'year' ? 'yr' : 'mo'} per membership`}
                  </span>
                </div>
              </div>
              <p className="membershipdescription">
                Give a partner, friend, or family member their own Aris experience—billed to your plan.
              </p>
              <div className="membershipmodal-list">
                <div className="dot">•</div>
                <div className="membershipmodal-list-txt">
                  Add memberships for family, friends, or colleagues
                </div>
              </div>
              <div className="membershipmodal-list">
                <div className="dot">•</div>
                <div className="membershipmodal-list-txt">
                  Each member has their own personalized Aris experience
                </div>
              </div>
              <div className="membershipmodal-list">
                <div className="dot">•</div>
                <div className="membershipmodal-list-txt">
                  Billed at the same rate and schedule as your plan
                </div>
              </div>
        </div>
        <div className="membershiptotal-price">
          <span className="membership-total-price-txt">{billingCycle === 'yearly' || billingCycle === 'year' ? "Annual Total:" : "Monthly Total:"}</span>
          <span className="membership-total-price">{typeof totalPrice === 'number' ? `$${totalPrice.toFixed(2)}` : '-'}</span>
        </div>
      </div>
    </GenericModal>
  );
};

export default AddMembersModal;
